import React from "react";

import {CheckIcon, PlusIcon} from "@heroicons/react/16/solid";
import {
    CameraIcon,
    HomeIcon,
    UserGroupIcon,
    UserIcon,
} from "@heroicons/react/24/outline";
import {AnimatePresence, type Transition, motion} from "framer-motion";
import {produce} from "immer";
import {flushSync} from "react-dom";

import * as auth from "@client/features/auth";
import {
    type CaptureResult,
    type ProcessResult,
    resolveEntry,
    useCapture,
} from "@client/features/camera";
import * as dialogs from "@client/features/dialogs";
import * as journal from "@client/features/journal";
import * as orm from "@client/features/orm";
import {useProfile} from "@client/features/profile";
import {showFeelerIfFirstTime} from "@client/features/scenes";

import * as graphics from "@client/app/graphics";
import * as dates from "@client/dates";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import * as utils from "@client/utils";
import {formatUnit} from "@client/utils";

import rocketAnimation from "@client/animations/rocket.json";
import {getRoundedQuantity} from "@client/calculations";
import {AsyncButton} from "@client/components/Button";
import {Steps, WeighIn} from "@client/components/journal";
import {DecimalInput, QuickSelect} from "@client/forms";
import {Lottie} from "@client/hacks";
import * as hardware from "@client/hardware";
import type {JournalImplementation, Props} from "@client/scenes/Journal";
import {Spinner} from "@client/site/components";
import {
    Label,
    PFormHandler,
    PWidget,
    submitForm,
    useAutoPform,
} from "@client/site/forms";
import {useAppDispatch, useAppSelector} from "@client/store";
import {Schema, classNames, server} from "@reactivated";

function Breakfast() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            id="Farming-Wheat--Streamline-Ultimate"
            height="24"
            width="24"
        >
            <desc>Farming Wheat Streamline Icon: https://streamlinehq.com</desc>
            <path
                fill="#fff9bf"
                d="M14 5.5C14 6.881 12 9 12 9s-2 -2.119 -2 -3.5C10 4.119 12 2 12 2s2 2.119 2 3.5Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffef5e"
                d="M11.983 5.745c-0.7116 -0.02072 -1.3869 -0.31855 -1.882 -0.83 -0.062 0.18902 -0.0961 0.38611 -0.101 0.585C10 6.881 12 9 12 9s2 -2.119 2 -3.5c-0.0062 -0.20701 -0.0433 -0.41193 -0.11 -0.608 -0.4984 0.52456 -1.1838 0.83112 -1.907 0.853Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffef5e"
                d="m12.309 12.5 1.846 -0.769c2.039 -0.85 3.7 -3.192 2.845 -5.231l-2.537 0.769c-0.4849 0.20202 -0.9253 0.49758 -1.296 0.8698 -0.3707 0.37222 -0.6644 0.81381 -0.8644 1.29956 -0.2001 0.48575 -0.3024 1.00614 -0.3013 1.53144 0.0011 0.5253 0.1056 1.0453 0.3077 1.5302Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffef5e"
                d="m11.691 21.5 -1.846 -0.769C7.806 19.881 6.15 17.539 7 15.5l2.537 0.769c0.4849 0.202 0.9253 0.4976 1.296 0.8698 0.3707 0.3722 0.6644 0.8138 0.8644 1.2996 0.2001 0.4857 0.3024 1.0061 0.3013 1.5314 -0.0011 0.5253 -0.1056 1.0453 -0.3077 1.5302Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffef5e"
                d="m12.309 21.5 1.846 -0.769c2.039 -0.85 3.7 -3.192 2.845 -5.231l-2.537 0.769c-0.4849 0.202 -0.9253 0.4976 -1.296 0.8698 -0.3707 0.3722 -0.6644 0.8138 -0.8644 1.2996 -0.2001 0.4857 -0.3024 1.0061 -0.3013 1.5314 0.0011 0.5253 0.1056 1.0453 0.3077 1.5302Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffef5e"
                d="m11.691 17 -1.846 -0.769C7.806 15.381 6.15 13.039 7 11l2.537 0.769c0.4849 0.202 0.9253 0.4976 1.296 0.8698 0.3707 0.3722 0.6644 0.8138 0.8644 1.2996 0.2001 0.4857 0.3024 1.0061 0.3013 1.5314 -0.0011 0.5253 -0.1056 1.0453 -0.3077 1.5302Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffef5e"
                d="m12.309 17 1.846 -0.769c2.039 -0.85 3.7 -3.192 2.845 -5.231l-2.537 0.769c-0.4849 0.202 -0.9253 0.4976 -1.296 0.8698 -0.3707 0.3722 -0.6644 0.8138 -0.8644 1.2996 -0.2001 0.4857 -0.3024 1.0061 -0.3013 1.5314 0.0011 0.5253 0.1056 1.0453 0.3077 1.5302Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffef5e"
                d="m11.691 12.5 -1.846 -0.769C7.806 10.881 6.15 8.539 7 6.5l2.537 0.769c0.4849 0.20202 0.9253 0.49758 1.296 0.8698 0.3707 0.37222 0.6644 0.81381 0.8644 1.29956 0.2001 0.48575 0.3024 1.00614 0.3013 1.53144 -0.0011 0.5253 -0.1056 1.0453 -0.3077 1.5302Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#fff9bf"
                d="M9.537 9.312c0.5647 0.23683 1.0671 0.60079 1.4681 1.0635 0.401 0.4628 0.6898 1.0119 0.8439 1.6045 0.2496 -0.9321 0.1529 -1.923 -0.2723 -2.78921 -0.4251 -0.86621 -1.1497 -1.54898 -2.0397 -1.92179L7 6.5c-0.28354 0.70282 -0.30167 1.4848 -0.051 2.2 0.073 -0.214 2.588 0.612 2.588 0.612Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#fff9bf"
                d="M9.537 13.6c0.5822 0.2436 1.0983 0.6218 1.5058 1.1037 0.4075 0.482 0.6948 1.0537 0.8382 1.6683 0.2189 -0.9211 0.1034 -1.8901 -0.326 -2.734 -0.4293 -0.8438 -1.1445 -1.5077 -2.018 -1.873L7 11c-0.26843 0.6643 -0.29846 1.4011 -0.085 2.085 0.027 -0.085 2.622 0.515 2.622 0.515Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#fff9bf"
                d="M9.537 18.269c0.5683 0.2382 1.0735 0.605 1.4759 1.0716 0.4024 0.4666 0.691 1.0203 0.8431 1.6174 0.243 -0.9298 0.1422 -1.9161 -0.2839 -2.7776 -0.426 -0.8614 -1.1487 -1.5401 -2.0351 -1.9114L7 15.5c-0.27948 0.6936 -0.30007 1.4645 -0.058 2.172 0.02 -0.057 2.595 0.597 2.595 0.597Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#fff9bf"
                d="M14.463 9.312c-0.5647 0.23683 -1.0671 0.60079 -1.4681 1.0635 -0.401 0.4628 -0.6898 1.0119 -0.8439 1.6045 -0.2496 -0.9321 -0.1529 -1.923 0.2723 -2.78921 0.4251 -0.86621 1.1497 -1.54898 2.0397 -1.92179L17 6.5c0.2835 0.70282 0.3017 1.4848 0.051 2.2 -0.073 -0.214 -2.588 0.612 -2.588 0.612Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#fff9bf"
                d="M14.463 13.6c-0.5822 0.2436 -1.0983 0.6218 -1.5058 1.1037 -0.4075 0.482 -0.6948 1.0537 -0.8382 1.6683 -0.2189 -0.9211 -0.1034 -1.8901 0.326 -2.734 0.4293 -0.8438 1.1445 -1.5077 2.018 -1.873L17 11c0.2684 0.6643 0.2985 1.4011 0.085 2.085 -0.027 -0.085 -2.622 0.515 -2.622 0.515Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#fff9bf"
                d="M14.463 18.269c-0.5683 0.2382 -1.0735 0.605 -1.4759 1.0716 -0.4024 0.4666 -0.691 1.0203 -0.8431 1.6174 -0.243 -0.9298 -0.1422 -1.9161 0.2839 -2.7776 0.426 -0.8614 1.1487 -1.5401 2.0351 -1.9114L17 15.5c0.2795 0.6936 0.3001 1.4645 0.058 2.172 -0.02 -0.057 -2.595 0.597 -2.595 0.597Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m11.691 21.5 -1.846 -0.769C7.806 19.881 6.15 17.539 7 15.5l2.537 0.769c0.4849 0.202 0.9253 0.4976 1.296 0.8698 0.3707 0.3722 0.6644 0.8138 0.8644 1.2996 0.2001 0.4857 0.3024 1.0061 0.3013 1.5314 -0.0011 0.5253 -0.1056 1.0453 -0.3077 1.5302v0Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m11.691 17 -1.846 -0.769C7.806 15.381 6.15 13.039 7 11l2.537 0.769c0.4849 0.202 0.9253 0.4976 1.296 0.8698 0.3707 0.3722 0.6644 0.8138 0.8644 1.2996 0.2001 0.4857 0.3024 1.0061 0.3013 1.5314 -0.0011 0.5253 -0.1056 1.0453 -0.3077 1.5302Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m11.691 12.5 -1.846 -0.769C7.806 10.881 6.15 8.539 7 6.5l2.537 0.769c0.4849 0.20202 0.9253 0.49758 1.296 0.8698 0.3707 0.37222 0.6644 0.81381 0.8644 1.29956 0.2001 0.48575 0.3024 1.00614 0.3013 1.53144 -0.0011 0.5253 -0.1056 1.0453 -0.3077 1.5302Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v14.5"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 6.5v-3"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7 6.5v-3"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14 5.5C14 6.881 12 9 12 9s-2 -2.119 -2 -3.5C10 4.119 12 2 12 2s2 2.119 2 3.5Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 2V0.5"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m12.309 21.5 1.846 -0.769c2.039 -0.85 3.7 -3.192 2.845 -5.231l-2.537 0.769c-0.4849 0.202 -0.9253 0.4976 -1.296 0.8698 -0.3707 0.3722 -0.6644 0.8138 -0.8644 1.2996 -0.2001 0.4857 -0.3024 1.0061 -0.3013 1.5314 0.0011 0.5253 0.1056 1.0453 0.3077 1.5302v0Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m12.309 17 1.846 -0.769c2.039 -0.85 3.7 -3.192 2.845 -5.231l-2.537 0.769c-0.4849 0.202 -0.9253 0.4976 -1.296 0.8698 -0.3707 0.3722 -0.6644 0.8138 -0.8644 1.2996 -0.2001 0.4857 -0.3024 1.0061 -0.3013 1.5314 0.0011 0.5253 0.1056 1.0453 0.3077 1.5302v0Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m12.309 12.5 1.846 -0.769c2.039 -0.85 3.7 -3.192 2.845 -5.231l-2.537 0.769c-0.4849 0.20202 -0.9253 0.49758 -1.296 0.8698 -0.3707 0.37222 -0.6644 0.81381 -0.8644 1.29956 -0.2001 0.48575 -0.3024 1.00614 -0.3013 1.53144 0.0011 0.5253 0.1056 1.0453 0.3077 1.5302v0Z"
                strokeWidth="1"
            ></path>
        </svg>
    );
}

function Lunch() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="-0.5 -0.98 24 24"
            id="Vegetables-Salad--Streamline-Ultimate"
            height="24"
            width="24"
        >
            <desc>Vegetables Salad Streamline Icon: https://streamlinehq.com</desc>
            <path
                fill="#78eb7b"
                d="M14.652839999999998 5.7205416c-0.0011039999999999997 -0.1270612 -0.01656 -0.2535888 -0.046 -0.3771999999999999Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#78eb7b"
                d="M20.908839999999998 8.314969199999998c0.159252 0.2753284 0.257876 0.5814676 0.28934 0.8979107999999999 0.031464 0.31657199999999996 -0.004968 0.636088 -0.10681199999999999 0.9373879999999999 -0.10193599999999998 0.3013 -0.266892 0.577392 -0.484012 0.809784 -0.21711999999999998 0.23248399999999997 -0.48125199999999996 0.41593199999999997 -0.7749159999999999 0.5381079999999999H0.6780409199999999c0.09520344 -0.184276 0.21946047999999999 -0.35199199999999997 0.36799908 -0.4968 -0.14366075999999997 -0.201204 -0.25250871999999996 -0.42513199999999995 -0.32199908 -0.6623999999999999 -0.13291699999999998 -0.41381599999999996 -0.12859668 -0.859464 0.012317879999999998 -1.2705844 0.14091363999999998 -0.41113879999999997 0.4108812 -0.7657436 0.7696812 -1.0110063999999999 -0.11362919999999999 -0.3036092 -0.1656184 -0.6268235999999999 -0.15292239999999999 -0.9507556 0.012695999999999999 -0.323932 0.08981959999999999 -0.6420864 0.22686279999999998 -0.9358792 0.1370432 -0.2937836 0.33126439999999996 -0.5573176 0.5713016 -0.7752011999999999 0.24004639999999997 -0.2178744 0.5211064 -0.38573759999999996 0.8267579999999999 -0.4937639999999999 0.3171516 -0.07221999999999999 0.6454536 -0.0809416 0.9659907999999999 -0.025677199999999997 0.320528 0.05526439999999999 0.6269615999999999 0.1734292 0.9016092 0.3476772 0.3166272 0.1997688 0.5789468 0.4747291999999999 0.7636 0.8003999999999999 0.042007199999999995 -0.4165944 0.1911944 -0.8152119999999999 0.43303479999999994 -1.1570196 0.24184039999999998 -0.3417984 0.5680907999999999 -0.6151395999999999 0.9469651999999998 -0.7933803999999999 -0.08473199999999999 -0.3923524 -0.0902888 -0.7976675999999999 -0.0163392 -1.1921912 0.07394959999999999 -0.39453279999999996 0.22591519999999998 -0.7703251999999999 0.446982 -1.1053524 0.22106679999999998 -0.33503639999999996 0.5068003999999999 -0.6225639999999999 0.8404383999999999 -0.8457375999999999 0.33363799999999993 -0.22316992 0.7084644 -0.37749256 1.1025188 -0.45391788 0.400016 -0.031769439999999996 0.8024239999999999 0.01684888 1.183396 0.142991s0.732872 0.32725687999999997 1.034908 0.59149008c0.30203599999999997 0.26424239999999993 0.548136 0.5862515999999999 0.7238559999999999 0.9470756 0.17562799999999998 0.360824 0.277288 0.7531579999999999 0.299 1.1538823999999999 0.502228 0.0928924 0.963792 0.3376676 1.3224999999999998 0.7012608 0.35861599999999993 0.3636024 0.5970799999999999 0.8285059999999999 0.6831 1.3319391999999999 -0.014628 -0.31087719999999996 0.04001999999999999 -0.6211287999999999 0.16017199999999998 -0.9082423999999999 0.12006 -0.28712279999999996 0.30249599999999993 -0.5439223999999999 0.534152 -0.751778 0.231656 -0.2078648 0.506644 -0.3615692 0.8050919999999999 -0.44998119999999997 0.29835599999999995 -0.088412 0.6127199999999999 -0.10932359999999999 0.9201839999999999 -0.06119839999999999 0.127328 -0.2999292 0.31464 -0.5706852 0.5503439999999999 -0.7956435999999999 0.23570399999999997 -0.2249676 0.5149239999999999 -0.39939959999999997 0.820456 -0.5125964000000001 0.303508 -0.11364759999999999 0.626612 -0.16567359999999998 0.9504519999999999 -0.15303279999999997 0.323748 0.012631599999999998 0.6418839999999999 0.08968159999999999 0.9355479999999998 0.2266328 0.279128 0.16828639999999997 0.5214559999999999 0.39100919999999995 0.7126319999999999 0.6549204 0.191268 0.26391119999999996 0.327428 0.5636011999999999 0.40038399999999996 0.881222 0.072956 0.3176208 0.081236 0.6466772 0.02438 0.9675731999999998 -0.056856 0.3209052 -0.17774399999999999 0.627072 -0.35539599999999993 0.9002844 0.189152 0.2032464 0.33543199999999995 0.4424556 0.43028399999999994 0.7034043999999999 0.09475999999999998 0.2609488 0.136068 0.5383011999999999 0.12153199999999999 0.8155524 -0.014628 0.2772512 -0.084824 0.5487247999999999 -0.20654 0.798284 -0.121624 0.24955919999999998 -0.292284 0.4720888 -0.501676 0.6543591999999999h-0.00276Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffdda1"
                d="M0.45999999999999996 13.34c0 4.572399999999999 4.6 8.28 10.579999999999998 8.28s10.579999999999998 -3.7076 10.579999999999998 -8.28H0.45999999999999996Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffbc44"
                d="M11.04 17.939999999999998c-4.2504 0 -7.79332 -1.8777199999999998 -9.49348 -4.6H0.45999999999999996c0 4.572399999999999 4.6 8.28 10.579999999999998 8.28s10.579999999999998 -3.7076 10.579999999999998 -8.28h-1.08652c-1.70016 2.72504 -5.243079999999999 4.6 -9.49348 4.6Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M0.45999999999999996 13.34c0 4.572399999999999 4.6 8.28 10.579999999999998 8.28s10.579999999999998 -3.7076 10.579999999999998 -8.28H0.45999999999999996Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.22116 10.08504c-0.1332896 -0.264408 -0.3229936 -0.49643199999999993 -0.5557168 -0.679512 -0.2327232 -0.18317199999999997 -0.5027892 -0.31295639999999997 -0.7911539999999999 -0.38032799999999994 0.0225308 -0.2765336 -0.010718 -0.5547875999999999 -0.09775919999999999 -0.8182295999999999 -0.08705039999999999 -0.2634512 -0.2261268 -0.5067267999999999 -0.4089768 -0.7154103999999999 -0.18284999999999998 -0.20867439999999998 -0.4057568 -0.37850639999999997 -0.6554816 -0.4994036 -0.249734 -0.1208972 -0.5211984 -0.1904032 -0.7983024 -0.20439639999999998 -0.0646392 -0.31857759999999996 -0.19248239999999997 -0.6209631999999999 -0.37595799999999996 -0.8892903999999999 -0.1834848 -0.2683364 -0.41888519999999996 -0.49716799999999994 -0.6923092 -0.6729892 -0.2734148 -0.17581199999999997 -0.5792963999999999 -0.295044 -0.8995668 -0.3506487999999999 -0.3202796 -0.055604799999999996 -0.6484527999999999 -0.04644159999999999 -0.9651259999999999 0.026928399999999998 -0.306544 0.10736399999999999 -0.5884963999999999 0.27497879999999997 -0.8292603999999999 0.49299119999999996 -0.24076399999999998 0.21800319999999998 -0.43546359999999995 0.48197879999999993 -0.5726356 0.776388 -0.137172 0.2944092 -0.2140472 0.6132903999999999 -0.22608079999999997 0.9378663999999999 -0.012042799999999998 0.324576 0.040985999999999995 0.6482779999999999 0.15597679999999997 0.9520344 -0.35953599999999997 0.244628 -0.63014204 0.59915 -0.7712847599999999 1.0104636 -0.14114272 0.4113044 -0.14522568 0.8573203999999999 -0.01163708 1.2711363999999998 0.06883255999999999 0.235428 0.17641276 0.45769999999999994 0.31832183999999997 0.6577999999999999 -0.14774647999999999 0.1449 -0.27025827999999996 0.313444 -0.36248184 0.49863999999999997"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.650079999999999 5.7242216c-0.012327999999999999 -0.5758555999999999 -0.22236399999999998 -1.1299347999999998 -0.594964 -1.5691887999999998 -0.3726 -0.439254 -0.884948 -0.7369383999999999 -1.4511159999999999 -0.8430511999999999 -0.021803999999999997 -0.4008256 -0.12364799999999998 -0.7932239999999999 -0.299552 -1.1540388 -0.175904 -0.36081479999999994 -0.42237199999999997 -0.6827411999999999 -0.7246839999999999 -0.9467719999999998 -0.30240399999999995 -0.2640308 -0.6545799999999999 -0.46482448 -1.0358279999999997 -0.5905231599999999 -0.381156 -0.12569776 -0.7837479999999999 -0.17375764 -1.1838559999999998 -0.14134235999999997 -0.39399 0.07602328 -0.7687795999999999 0.23012051999999997 -1.1023071999999998 0.45321592 -0.3335276 0.2230908 -0.6190312 0.5106736 -0.8397116 0.8458019999999999 -0.22068039999999997 0.3351376 -0.3720572 0.7110403999999999 -0.44523399999999996 1.1055732 -0.0731676 0.39453279999999996 -0.0666448 0.7997099999999999 0.019181999999999998 1.1916851999999998 -0.38376879999999997 0.179078 -0.7134599999999999 0.45627399999999996 -0.9557696 0.8035831999999999 -0.24230959999999999 0.3473092 -0.38865399999999994 0.7524312 -0.42423039999999995 1.1744168"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.836119999999998 11.5c0.29292799999999997 -0.12374 0.556232 -0.308292 0.772432 -0.541512 0.216108 -0.233128 0.38023599999999996 -0.50968 0.481436 -0.811072 0.1012 -0.301484 0.13707999999999998 -0.621 0.10543199999999998 -0.9373879999999999 -0.03174 -0.31643399999999994 -0.13036399999999998 -0.6224719999999999 -0.28934 -0.8978555999999999 0.21040399999999998 -0.18199439999999997 0.381984 -0.40452399999999994 0.504436 -0.6542856 0.122544 -0.24976159999999997 0.19347599999999998 -0.5216491999999999 0.208564 -0.7994247999999999 0.015088 -0.27777559999999996 -0.025944 -0.5557536 -0.12061199999999998 -0.8173279999999999 -0.094668 -0.2615836 -0.24104 -0.5014092 -0.43046799999999996 -0.7051615999999999 0.176364 -0.2730468 0.296148 -0.57868 0.352452 -0.8988215999999999 0.056212 -0.3201508 0.047748 -0.6483239999999999 -0.025023999999999998 -0.9651352 -0.07268 -0.31681119999999996 -0.208196 -0.6158296 -0.398452 -0.8793911999999999 -0.190256 -0.2635616 -0.43138799999999994 -0.48633039999999994 -0.709136 -0.6551319999999999 -0.294308 -0.1379724 -0.61318 -0.21557439999999997 -0.93794 -0.2282244 -0.32475999999999994 -0.01265 -0.648784 0.039909599999999996 -0.952844 0.1545692 -0.304152 0.1146596 -0.582176 0.2891008 -0.817788 0.5130195999999999 -0.23551999999999998 0.22391879999999997 -0.42384399999999994 0.49277039999999994 -0.5537479999999999 0.7906755999999999 -0.307648 -0.0470212 -0.62192 -0.0251896 -0.9200919999999999 0.06394 -0.298172 0.08912959999999999 -0.572884 0.24333079999999999 -0.8042639999999999 0.4514716 -0.23137999999999997 0.2081408 -0.41363199999999994 0.4650416 -0.5336919999999999 0.7521735999999999 -0.12015199999999998 0.28713199999999994 -0.174984 0.5973284 -0.160632 0.9082148"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffbfc5"
                d="M17.484599999999997 11.5c0.27241199999999993 -0.202768 0.49817999999999996 -0.461472 0.6623079999999999 -0.758724 0.164036 -0.29734399999999994 0.26256799999999997 -0.6263359999999999 0.28897199999999995 -0.9648039999999999 0.026403999999999997 -0.33855999999999997 -0.020055999999999997 -0.6788128 -0.136068 -0.9979239999999999 -0.11601199999999998 -0.3191112 -0.29909199999999997 -0.6096931999999999 -0.536728 -0.8522051999999999 -0.23763599999999996 -0.24250279999999996 -0.524492 -0.43133279999999996 -0.8412479999999999 -0.5537755999999999 -0.316664 -0.12245199999999999 -0.6559599999999999 -0.17568319999999998 -0.9948879999999999 -0.1561332 -0.33902 0.0195592 -0.6699439999999999 0.11145799999999999 -0.9704159999999998 0.269514 -0.30056399999999994 0.158056 -0.5637759999999999 0.3786168 -0.7719719999999999 0.6468519999999999 -0.09043599999999999 0.11520239999999998 -0.1702 0.23837199999999997 -0.23828 0.368l0.5602799999999999 2.9955199999999995"
                strokeWidth="1"
            ></path>
            <path
                fill="#ff808c"
                d="M16.198439999999998 10.15772c-0.401396 -0.00027599999999999993 -0.798192 -0.085744 -1.1640759999999999 -0.250884 -0.365884 -0.165048 -0.692576 -0.40599599999999997 -0.958364 -0.7068359999999999l0.43056 2.3h2.97804c0.366436 -0.270572 0.647496 -0.6406879999999999 0.809784 -1.0663719999999999s0.198904 -0.8889039999999999 0.10561599999999999 -1.3348187999999999c-0.264868 0.3294428 -0.599932 0.5955068 -0.980904 0.7787707999999999 -0.38087999999999994 0.18317199999999997 -0.7980079999999999 0.278944 -1.220656 0.28013999999999994Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.484599999999997 11.5c0.27241199999999993 -0.202768 0.49817999999999996 -0.461472 0.6623079999999999 -0.758724 0.164036 -0.29734399999999994 0.26256799999999997 -0.6263359999999999 0.28897199999999995 -0.9648039999999999 0.026403999999999997 -0.33855999999999997 -0.020055999999999997 -0.6788128 -0.136068 -0.9979239999999999 -0.11601199999999998 -0.3191112 -0.29909199999999997 -0.6096931999999999 -0.536728 -0.8522051999999999 -0.23763599999999996 -0.24250279999999996 -0.524492 -0.43133279999999996 -0.8412479999999999 -0.5537755999999999 -0.316664 -0.12245199999999999 -0.6559599999999999 -0.17568319999999998 -0.9948879999999999 -0.1561332 -0.33902 0.0195592 -0.6699439999999999 0.11145799999999999 -0.9704159999999998 0.269514 -0.30056399999999994 0.158056 -0.5637759999999999 0.3786168 -0.7719719999999999 0.6468519999999999 -0.09043599999999999 0.11520239999999998 -0.1702 0.23837199999999997 -0.23828 0.368l0.5602799999999999 2.9955199999999995"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.14 8.5928276c0.5751472 0.8626564 0.8941295999999999 1.8707004 0.9199999999999999 2.9071724"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m9.66 3.5328275999999996 0.06531999999999999 0.16468c0.5428919999999999 1.3566411999999999 0.8326 2.8012895999999996 0.85468 4.262359999999999"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18.86 4.912827599999999c-0.7386679999999999 0.7433323999999999 -1.3911319999999998 1.5676523999999998 -1.9448799999999997 2.4573199999999997"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffbfc5"
                d="M9.413439999999998 11.5c-0.17526 -0.41942799999999997 -0.2442692 -0.8757479999999999 -0.20074399999999998 -1.328296 0.043424 -0.45254799999999995 0.19807599999999997 -0.8873399999999999 0.45006399999999996 -1.2657083999999998 0.25198799999999993 -0.3784052 0.5934919999999999 -0.6887028 0.9942439999999999 -0.9033296 0.40075199999999994 -0.21461759999999996 0.848332 -0.326922 1.3029959999999998 -0.326922 0.45466399999999996 0 0.9022439999999999 0.11230439999999998 1.3029959999999998 0.326922 0.40075199999999994 0.21462679999999998 0.7422559999999999 0.5249244 0.9942439999999999 0.9033296 0.25198799999999993 0.3783684 0.40663999999999995 0.8131604 0.45006399999999996 1.2657083999999998 0.043516 0.45254799999999995 -0.025483999999999996 0.9088679999999999 -0.20074399999999998 1.328296"
                strokeWidth="1"
            ></path>
            <path
                fill="#ff808c"
                d="M11.959999999999999 10.37116c-0.4733399999999999 -0.0005519999999999999 -0.93978 -0.113436 -1.360956 -0.329544s-0.7849439999999999 -0.529184 -1.0614039999999998 -0.913376c-0.19549999999999998 0.360824 -0.30810799999999994 0.7606559999999999 -0.329636 1.170516 -0.0214544 0.40976799999999997 0.04876 0.8191679999999999 0.20543599999999998 1.1984839999999999h5.093119999999999c0.15732 -0.37913199999999997 0.22779199999999997 -0.7886239999999999 0.206264 -1.1985759999999999 -0.021436 -0.409952 -0.134412 -0.809784 -0.330464 -1.170424 -0.276368 0.38428399999999996 -0.6402279999999999 0.69736 -1.0613119999999998 0.913468 -0.42117599999999994 0.216108 -0.8877079999999999 0.32899199999999995 -1.361048 0.32945199999999997Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.413439999999998 11.5c-0.17526 -0.41942799999999997 -0.2442692 -0.8757479999999999 -0.20074399999999998 -1.328296 0.043424 -0.45254799999999995 0.19807599999999997 -0.8873399999999999 0.45006399999999996 -1.2657083999999998 0.25198799999999993 -0.3784052 0.5934919999999999 -0.6887028 0.9942439999999999 -0.9033296 0.40075199999999994 -0.21461759999999996 0.848332 -0.326922 1.3029959999999998 -0.326922 0.45466399999999996 0 0.9022439999999999 0.11230439999999998 1.3029959999999998 0.326922 0.40075199999999994 0.21462679999999998 0.7422559999999999 0.5249244 0.9942439999999999 0.9033296 0.25198799999999993 0.3783684 0.40663999999999995 0.8131604 0.45006399999999996 1.2657083999999998 0.043516 0.45254799999999995 -0.025483999999999996 0.9088679999999999 -0.20074399999999998 1.328296"
                strokeWidth="1"
            ></path>
        </svg>
    );
}

function Dinner() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            id="Asian-Food-Pho--Streamline-Ultimate"
            height="24"
            width="24"
        >
            <desc>Asian Food Pho Streamline Icon: https://streamlinehq.com</desc>
            <path
                fill="#c2f3ff"
                d="M12.5 13c3.5899 0 6.5 -2.9101 6.5 -6.5C19 2.91015 16.0899 0 12.5 0 8.91015 0 6 2.91015 6 6.5c0 3.5899 2.91015 6.5 6.5 6.5Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffbc44"
                d="M8.908 19.6801c-0.92867 0.4767 -1.72498 1.1758 -2.318 2.035 -0.05199 0.0749 -0.08249 0.1626 -0.0882 0.2537 -0.0057 0.091 0.0136 0.1819 0.05583 0.2627 0.04223 0.0809 0.10577 0.1486 0.18375 0.196 0.07797 0.0473 0.16741 0.0724 0.25862 0.0726h10c0.0911 -0.0001 0.1805 -0.0251 0.2585 -0.0724 0.0779 -0.0472 0.1415 -0.1148 0.1838 -0.1955 0.0423 -0.0807 0.0618 -0.1715 0.0563 -0.2624 -0.0056 -0.091 -0.0358 -0.1787 -0.0876 -0.2537 -0.5935 -0.8587 -1.3897 -1.5577 -2.318 -2.035"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.908 19.6801c-0.92867 0.4767 -1.72498 1.1758 -2.318 2.035 -0.05199 0.0749 -0.08249 0.1626 -0.0882 0.2537 -0.0057 0.091 0.0136 0.1819 0.05583 0.2627 0.04223 0.0809 0.10577 0.1486 0.18375 0.196 0.07797 0.0473 0.16741 0.0724 0.25862 0.0726h10c0.0911 -0.0001 0.1805 -0.0251 0.2585 -0.0724 0.0779 -0.0472 0.1415 -0.1148 0.1838 -0.1955 0.0423 -0.0807 0.0618 -0.1715 0.0563 -0.2624 -0.0056 -0.091 -0.0358 -0.1787 -0.0876 -0.2537 -0.5935 -0.8587 -1.3897 -1.5577 -2.318 -2.035"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffdda1"
                d="M23 14.5c0.0983 0 0.1945 0.029 0.2764 0.0834 0.082 0.0544 0.1461 0.1317 0.1843 0.2223 0.0382 0.0906 0.0488 0.1905 0.0306 0.2871 -0.0183 0.0967 -0.0647 0.1858 -0.1333 0.2562C20.3082 18.2979 16.2422 19.9629 12 20c-4.2421 -0.0367 -8.30805 -1.7013 -11.358001 -4.65 -0.068981 -0.0704 -0.115629 -0.1596 -0.134083 -0.2564 -0.018455 -0.0968 -0.007894 -0.1969 0.030356 -0.2877 0.03825 -0.0908 0.102484 -0.1683 0.184633 -0.2227 0.082149 -0.0544 0.17855 -0.0834 0.277094 -0.0832H23Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffbc44"
                d="M23 14.5h-2.623c-2.529 1.5291 -5.4218 2.352 -8.377 2.383 -2.9555 -0.031 -5.84862 -0.8539 -8.378 -2.383H0.999999c-0.098544 -0.0002 -0.194945 0.0288 -0.277094 0.0832s-0.146383 0.1319 -0.184633 0.2227c-0.03825 0.0908 -0.048811 0.1909 -0.030356 0.2877 0.018454 0.0968 0.065102 0.186 0.134083 0.2564C3.69166 18.2991 7.7578 19.9638 12 20c4.2422 -0.0371 8.3082 -1.7021 11.358 -4.651 0.0686 -0.0704 0.115 -0.1595 0.1333 -0.2562 0.0182 -0.0966 0.0076 -0.1965 -0.0306 -0.2871 -0.0382 -0.0906 -0.1023 -0.1679 -0.1843 -0.2223 -0.0819 -0.0544 -0.1781 -0.0834 -0.2764 -0.0834Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M23 14.5c0.0983 0 0.1945 0.029 0.2764 0.0834 0.082 0.0544 0.1461 0.1317 0.1843 0.2223 0.0382 0.0906 0.0488 0.1905 0.0306 0.2871 -0.0183 0.0967 -0.0647 0.1858 -0.1333 0.2562C20.3082 18.2979 16.2422 19.9629 12 20c-4.2421 -0.0367 -8.30805 -1.7013 -11.358001 -4.65 -0.068981 -0.0704 -0.115629 -0.1596 -0.134083 -0.2564 -0.018455 -0.0968 -0.007894 -0.1969 0.030356 -0.2877 0.03825 -0.0908 0.102484 -0.1683 0.184633 -0.2227 0.082149 -0.0544 0.17855 -0.0834 0.277094 -0.0832H23Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 1c-0.46386 0.46428 -0.72441 1.09371 -0.72441 1.75S7.53614 4.03572 8 4.5c0.46336 0.46453 0.72358 1.09388 0.72358 1.75S8.46336 7.53547 8 8c-0.46386 0.46428 -0.72441 1.09371 -0.72441 1.75 0 0.6563 0.26055 1.2857 0.72441 1.75"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16 1c-0.4639 0.46428 -0.7244 1.09371 -0.7244 1.75S15.5361 4.03572 16 4.5c0.4634 0.46453 0.7236 1.09388 0.7236 1.75S16.4634 7.53547 16 8c-0.4639 0.46428 -0.7244 1.09371 -0.7244 1.75 0 0.6563 0.2605 1.2857 0.7244 1.75"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 1c-0.4639 0.46428 -0.7244 1.09371 -0.7244 1.75S11.5361 4.03572 12 4.5c0.4641 0.46415 0.7248 1.09363 0.7248 1.75 0 0.65637 -0.2607 1.28585 -0.7248 1.75 -0.4639 0.46428 -0.7244 1.09371 -0.7244 1.75 0 0.6563 0.2605 1.2857 0.7244 1.75"
                strokeWidth="1"
            ></path>
        </svg>
    );
}

function Snack() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            id="Ice-Cream-Cone--Streamline-Ultimate"
            height="24"
            width="24"
        >
            <desc>Ice Cream Cone Streamline Icon: https://streamlinehq.com</desc>
            <path
                fill="#ffbc44"
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m17 11 -4.531 12.173a0.5 0.5 0 0 1 -0.938 0L7 11V7h10v4Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#c77f67"
                d="m7 11 1.107 2.975a1.49 1.49 0 0 0 0.393 -1 1 1 0 1 1 2 0v1a1.5 1.5 0 1 0 3 0v-1a1 1 0 0 1 2 0c0.002 0.37 0.142 0.727 0.393 1L17 11V7H7v4Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m17 11 -4.531 12.173a0.5 0.5 0 0 1 -0.938 0L7 11V7h10v4Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#ffef5e"
                d="M10.5 9.5a1 1 0 0 0 -2 0 1.5 1.5 0 1 1 -3 0V7a6.5 6.5 0 1 1 13 0v2.5a1.5 1.5 0 1 1 -3 0 1 1 0 0 0 -2 0v1a1.5 1.5 0 1 1 -3 0v-1Z"
                strokeWidth="1"
            ></path>
            <path
                fill="#fff9bf"
                d="M12 0.5A6.5 6.5 0 0 0 5.5 7v2.5c0.002 0.15 0.027 0.299 0.073 0.441a6.492 6.492 0 0 1 12.854 0c0.046 -0.143 0.071 -0.291 0.073 -0.441V7A6.5 6.5 0 0 0 12 0.5Z"
                strokeWidth="1"
            ></path>
            <path
                stroke="#191919"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 9.5a1 1 0 0 0 -2 0 1.5 1.5 0 1 1 -3 0V7a6.5 6.5 0 1 1 13 0v2.5a1.5 1.5 0 1 1 -3 0 1 1 0 0 0 -2 0v1a1.5 1.5 0 1 1 -3 0v-1Z"
                strokeWidth="1"
            ></path>
        </svg>
    );
}

const LEARNING_WEEK_MEALS = ["Breakfast", "Lunch", "Dinner", "Snacks"] as const;
type LearningWeekMeal = (typeof LEARNING_WEEK_MEALS)[number];

// Week day selector - rolling 7-day window
function getWeekDays(startDate: Date, currentDayNumber: number) {
    // Days 1-7: Always show days 1-7
    // Day 8+: Show rolling window ending at current day (e.g., day 8 shows 2-8)
    const windowStart = Math.max(0, currentDayNumber - 7);

    return Array.from({length: 7}, (_, index) => {
        const dayNumber = windowStart + index + 1;
        const dateObj = dates.addDays(startDate, dayNumber - 1);
        const dayAbbrev = dates.format(dateObj, "EEE"); // "Mon", "Tue", etc.
        return {
            dayAbbrev,
            dayLabel: `Day ${dayNumber}`,
            dayNumber,
            calendarDate: parseInt(dates.format(dateObj, "d"), 10),
            dateString: dates.formatISO(dateObj, {representation: "date"}),
        };
    });
}

function ProgressRing({progress}: {progress: number}) {
    const strokeWidth = 8;
    // Using viewBox coordinates, radius calculated from 100x100 viewBox
    const viewBoxSize = 100;
    const radius = (viewBoxSize - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - progress * circumference;

    return (
        <svg
            viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
            className="absolute inset-0 size-full"
            style={{transform: "rotate(-90deg)"}}
        >
            {/* Background circle (light gray) */}
            <circle
                cx={viewBoxSize / 2}
                cy={viewBoxSize / 2}
                r={radius}
                fill="none"
                stroke="#e5e5e5"
                strokeWidth={strokeWidth}
            />
            {/* Progress arc */}
            <circle
                cx={viewBoxSize / 2}
                cy={viewBoxSize / 2}
                r={radius}
                fill="none"
                className="text-indigo-600"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
            />
        </svg>
    );
}

function DashedCircle({indigo = false}: {indigo?: boolean}) {
    const strokeWidth = 5;
    const viewBoxSize = 100;
    const radius = (viewBoxSize - strokeWidth) / 2;
    const dashLength = 12;
    const gapLength = 8;

    return (
        <svg
            viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
            className="absolute inset-0 size-full"
        >
            <circle
                cx={viewBoxSize / 2}
                cy={viewBoxSize / 2}
                r={radius}
                fill="none"
                stroke={indigo ? "#4f46e5" : "#d1d5db"}
                strokeWidth={strokeWidth}
                strokeDasharray={`${dashLength} ${gapLength}`}
            />
        </svg>
    );
}

interface DaySelectorProps {
    journal: server.journal.day.Journal.output;
}

// Day abbreviations (Mon, Tue) with day number in circle
function DaySelector({journal}: DaySelectorProps) {
    const learningWeek = journal.learning_week;

    const learningWeekStartDate = React.useMemo(
        () => (learningWeek != null ? dates.parseISO(learningWeek.start_date) : null),
        [learningWeek?.start_date],
    );

    const currentDate = dates.parseISO(journal.day.key);
    const currentDayNumber = learningWeek?.current_day_number ?? 1;

    const weekDays = React.useMemo(
        () =>
            learningWeekStartDate != null
                ? getWeekDays(learningWeekStartDate, currentDayNumber)
                : null,
        [learningWeekStartDate?.getTime(), currentDayNumber],
    );

    // Calculate which index in the 7-day window corresponds to the current journal date
    const windowStart = Math.max(0, currentDayNumber - 7);
    const currentJournalDayNumber =
        learningWeekStartDate != null
            ? dates.differenceInDays(currentDate, learningWeekStartDate) + 1
            : 1;
    // Clamp to valid window range
    const clampedDayNumber = Math.max(
        windowStart + 1,
        Math.min(currentDayNumber, currentJournalDayNumber),
    );
    const currentDayIndex = clampedDayNumber - windowStart - 1;

    const [selectedIndex, setSelectedIndex] = React.useState(currentDayIndex);

    const currentDayStatus = React.useMemo(() => {
        const meals = journal.day.meals;
        const measurements = journal.day.measurements;

        const hasMeals = meals.some((meal) => meal.entries.length > 0);
        const hasWeight = measurements.some(
            (measurement) => measurement.weight != null,
        );

        return {hasMeals, hasWeight};
    }, [journal.day.meals, journal.day.measurements]);

    if (weekDays == null || learningWeek == null) {
        return null;
    }

    const dayStatuses = learningWeek.my_status.days;

    return (
        <div className="relative -mx-1 grid grid-cols-7">
            <motion.div
                className="absolute inset-y-0 rounded-2xl bg-white"
                style={{width: `calc(100% / 7)`}}
                initial={false}
                animate={{left: `calc(${selectedIndex} * 100% / 7)`}}
                transition={{type: "spring", stiffness: 500, damping: 35}}
            />
            {weekDays.map((item, index) => {
                const isSelected = index === selectedIndex;

                // For the selected day, use the dynamically calculated status
                // BUT only if the journal data has loaded for this day
                // Otherwise fall back to backend status to avoid flicker
                const journalMatchesSelected = journal.day.key === item.dateString;
                // dayStatuses is indexed by actual day number - 1 (0-based)
                const backendDayStatus = dayStatuses[item.dayNumber - 1];
                const dayStatus =
                    isSelected && journalMatchesSelected
                        ? currentDayStatus
                        : {
                              hasMeals: backendDayStatus.has_logged_meals,
                              hasWeight: backendDayStatus.has_logged_weight,
                          };

                const hasComplete = dayStatus.hasMeals && dayStatus.hasWeight;
                const hasPartial =
                    !hasComplete && (dayStatus.hasMeals || dayStatus.hasWeight);

                return (
                    <button
                        key={item.dateString}
                        type="button"
                        onClick={() => {
                            void hardware.haptic();
                            setSelectedIndex(index);
                            router.push("journal.journal", {key: item.dateString});
                        }}
                        className="
                            relative z-10 flex flex-col items-center gap-1 rounded-2xl
                            px-1 py-2
                        "
                    >
                        <span
                            className={classNames(
                                "text-sm font-medium",
                                isSelected ? "text-gray-900" : "text-gray-500",
                            )}
                        >
                            {item.dayAbbrev}
                        </span>
                        <div className="relative size-10">
                            {/* Show progress rings based on completion status */}
                            {hasComplete ? (
                                <div className="absolute inset-0">
                                    <ProgressRing progress={1} />
                                </div>
                            ) : hasPartial ? (
                                <div className="absolute inset-0">
                                    <DashedCircle indigo />
                                </div>
                            ) : (
                                <div className="absolute inset-0">
                                    <DashedCircle />
                                </div>
                            )}
                            {!isSelected && hasComplete ? (
                                <div
                                    className="
                                        absolute inset-0 z-10 flex items-center
                                        justify-center rounded-full bg-indigo-600
                                    "
                                >
                                    <CheckIcon className="size-6 text-white" />
                                </div>
                            ) : (
                                <span
                                    className={classNames(
                                        "text-sm font-light absolute inset-0 z-10 flex items-center justify-center",
                                        isSelected
                                            ? "text-gray-900 font-normal"
                                            : "text-gray-600",
                                    )}
                                >
                                    {item.dayNumber}
                                </span>
                            )}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

// Group sub-tabs
type GroupTab = "today" | "week";

const GROUP_TABS: {id: GroupTab; label: string}[] = [
    {id: "today", label: "Today"},
    {id: "week", label: "Week"},
];

interface GroupTabSectionProps {
    selectedTab: GroupTab;
    onSelectTab: (tab: GroupTab) => void;
}

function GroupTabSection({selectedTab, onSelectTab}: GroupTabSectionProps) {
    const selectedIndex = GROUP_TABS.findIndex((tab) => tab.id === selectedTab);

    return (
        <div className="flex justify-center pt-2">
            <div
                className="
                    inline-flex items-center gap-1 rounded-3xl border border-gray-100/80
                    bg-white/50 p-1.5 backdrop-blur-md
                "
            >
                <motion.div
                    className="absolute inset-y-1.5 w-16 rounded-xl bg-gray-200"
                    initial={false}
                    animate={{left: 6 + selectedIndex * 68}}
                    transition={{type: "spring", stiffness: 500, damping: 35}}
                />
                {GROUP_TABS.map(({id, label}) => {
                    const isSelected = selectedTab === id;

                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => {
                                void hardware.haptic();
                                onSelectTab(id);
                            }}
                            className="
                                relative z-10 flex w-16 items-center justify-center
                                py-1.5
                            "
                        >
                            <span
                                className={classNames(
                                    "text-xs font-medium",
                                    isSelected ? "text-gray-900" : "text-gray-600",
                                )}
                            >
                                {label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// Multi-state delete button
const DELETE_STATES = {
    idle: "Delete",
    confirm: "Tap to confirm",
    processing: "Deleting",
    success: "Deleted",
    error: "Error",
} as const;

type DeleteState = keyof typeof DELETE_STATES;

const SPRING_CONFIG: Transition = {
    type: "spring",
    stiffness: 600,
    damping: 30,
};

function DeleteButtonLabel({state}: {state: DeleteState}) {
    const [labelWidth, setLabelWidth] = React.useState(0);
    const measureRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (measureRef.current) {
            const {width} = measureRef.current.getBoundingClientRect();
            setLabelWidth(width);
        }
    }, [state]);

    return (
        <>
            <div ref={measureRef} className="invisible absolute whitespace-nowrap">
                {DELETE_STATES[state]}
            </div>
            <motion.span
                layout
                style={{position: "relative"}}
                animate={{width: labelWidth}}
                transition={SPRING_CONFIG}
            >
                <AnimatePresence mode="sync" initial={false}>
                    <motion.div
                        key={state}
                        style={{textWrap: "nowrap"}}
                        initial={{
                            y: -20,
                            opacity: 0,
                            filter: "blur(8px)",
                            position: "absolute",
                        }}
                        animate={{
                            y: 0,
                            opacity: 1,
                            filter: "blur(0px)",
                            position: "relative",
                        }}
                        exit={{
                            y: 20,
                            opacity: 0,
                            filter: "blur(8px)",
                            position: "absolute",
                        }}
                        transition={{duration: 0.2, ease: "easeInOut"}}
                    >
                        {DELETE_STATES[state]}
                    </motion.div>
                </AnimatePresence>
            </motion.span>
        </>
    );
}

function MultiStateDeleteButton({
    onDelete,
    onClose,
}: {
    onDelete: () => Promise<void>;
    onClose: () => void;
}) {
    const [state, setState] = React.useState<DeleteState>("idle");
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleClick = async () => {
        void hardware.haptic();
        if (state === "idle") {
            setState("confirm");
            timeoutRef.current = setTimeout(() => {
                flushSync(() => setState("idle"));
            }, 3000);
        } else if (state === "confirm") {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setState("processing");
            try {
                await onDelete();
                setState("success");
                setTimeout(() => {
                    onClose();
                }, 500);
            } catch {
                setState("error");
                setTimeout(() => {
                    flushSync(() => setState("idle"));
                }, 2000);
            }
        }
    };

    const bgColor = {
        idle: "bg-red-100 hover:bg-red-200 text-red-700",
        confirm: "bg-red-500 hover:bg-red-600 text-white",
        processing: "bg-gray-400 text-white",
        success: "bg-green-500 text-white",
        error: "bg-red-600 text-white",
    }[state];

    return (
        <motion.button
            type="button"
            onClick={handleClick}
            disabled={state === "processing" || state === "success"}
            className={classNames(
                "flex items-center justify-center overflow-hidden rounded-full px-5 py-3 font-medium transition-colors",
                bgColor,
                "disabled:cursor-not-allowed",
            )}
            style={{gap: state === "idle" ? 0 : 8, willChange: "transform, filter"}}
        >
            <DeleteButtonLabel state={state} />
        </motion.button>
    );
}

interface RegularEntryViewProps {
    entry: server.journal.nutrition.ExpandedEntry.output;
    imageUrl: string | null;
    isBarcode: boolean;
    onDelete: () => Promise<void>;
    onClose: () => void;
    journal: server.journal.day.Journal.output;
    setJournal: (journal: server.journal.day.Journal.output) => void;
}

type AnalysisResult = Schema["rpc_reprocess_queued_image_output"];

function RegularEntryView({
    entry,
    imageUrl,
    isBarcode,
    onDelete,
    onClose,
    journal: journalProp,
    setJournal,
}: RegularEntryViewProps) {
    const [quantity, setQuantity] = React.useState<string | null>(entry.quantity);
    const [unit, setUnit] = React.useState<string>(entry.unit);
    const [isSaving, setIsSaving] = React.useState(false);

    // Correction mode state
    const [isCorrectMode, setIsCorrectMode] = React.useState(false);
    const [description, setDescription] = React.useState("");
    const [analysis, setAnalysis] = React.useState<AnalysisResult | null>(null);
    const [correctionError, setCorrectionError] = React.useState<string | null>(null);

    const isPhotoEntry =
        entry.nutritional_vision_prompt != null &&
        entry.nutritional_vision_prompt.type !== "BARCODE";
    const promptUuid = entry.nutritional_vision_prompt?.uuid;

    const handleSave = () => {
        if (quantity == null) {
            return;
        }

        setIsSaving(true);

        const updatedDay = produce(journalProp.day, (draft) => {
            for (const meal of draft.meals) {
                const entryIndex = meal.entries.findIndex((e) => e.uuid === entry.uuid);
                if (entryIndex !== -1) {
                    meal.entries[entryIndex].quantity = quantity;
                    meal.entries[entryIndex].unit = unit;
                    break;
                }
            }
        });

        const updatedJournal = {...journalProp, day: updatedDay};
        setJournal(updatedJournal);

        void server.journal.day.update_day(
            {user_id: updatedDay.user_id, key: updatedDay.key},
            {...updatedDay, deleted_meal_uuids: []},
        );

        setIsSaving(false);
        onClose();
    };

    const handleAnalyze = async () => {
        if (promptUuid == null) return;

        setCorrectionError(null);
        const result = await server.journal.nutritional_ai.reprocess_queued_image({
            prompt_uuid: promptUuid,
            description,
        });

        if (result.type === "success") {
            setAnalysis(result.data);
        } else {
            setCorrectionError("Failed to analyze entry");
        }
    };

    const handleApproveCorrection = async () => {
        if (analysis == null) return;

        const result = await server.journal.day.replace_entry({
            target_entry_uuid: entry.uuid,
            replacement: analysis,
        });

        if (result.type === "success") {
            // Update the journal with the new entry
            const updatedDay = produce(journalProp.day, (draft) => {
                for (const meal of draft.meals) {
                    const entryIndex = meal.entries.findIndex(
                        (e) => e.uuid === entry.uuid,
                    );
                    if (entryIndex !== -1) {
                        meal.entries[entryIndex] = analysis;
                        break;
                    }
                }
            });

            const updatedJournal = {...journalProp, day: updatedDay};
            setJournal(updatedJournal);
            onClose();
        } else {
            setCorrectionError("Failed to replace entry");
        }
    };

    // Correction mode UI
    if (isCorrectMode) {
        return (
            <>
                {/* Image preview */}
                {imageUrl != null && (
                    <div className="aspect-square bg-gray-100">
                        <img
                            src={imageUrl}
                            alt={entry.name}
                            className="size-full object-cover"
                        />
                    </div>
                )}

                <div className="space-y-4 p-4">
                    {analysis == null ? (
                        <>
                            <h3 className="text-center text-lg font-medium">
                                Correct Entry
                            </h3>
                            <p className="text-center text-sm text-gray-600">
                                Original: {entry.name}
                            </p>

                            <div>
                                <label className="mb-1 block text-sm font-medium">
                                    Description (help the AI understand the image)
                                </label>
                                <textarea
                                    className="w-full rounded-md border p-2 text-base"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="E.g., 'This is a chicken salad with ranch dressing, about 2 cups'"
                                />
                            </div>

                            {correctionError != null && (
                                <p className="text-center text-sm text-red-500">
                                    {correctionError}
                                </p>
                            )}

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        void hardware.haptic();
                                        setIsCorrectMode(false);
                                        setDescription("");
                                        setCorrectionError(null);
                                    }}
                                    className="
                                        flex-1 rounded-full bg-gray-100 px-5 py-3
                                        font-medium text-gray-700 transition-colors
                                        hover:bg-gray-200
                                    "
                                >
                                    Cancel
                                </button>
                                <AsyncButton
                                    onClick={handleAnalyze}
                                    disabled={description.trim() === ""}
                                    className="
                                        flex-1 rounded-full bg-indigo-500 px-5 py-3
                                        font-medium text-white transition-colors
                                        hover:bg-indigo-600
                                        disabled:cursor-not-allowed disabled:bg-gray-300
                                    "
                                >
                                    Analyze
                                </AsyncButton>
                            </div>
                        </>
                    ) : (
                        <>
                            <h3 className="text-center text-lg font-medium">
                                New: {analysis.name}
                            </h3>

                            {analysis.expanded_recipe.length > 0 && (
                                <p className="text-center text-sm text-gray-600">
                                    {analysis.expanded_recipe.length}{" "}
                                    {utils.pluralize(
                                        analysis.expanded_recipe.length,
                                        "ingredient",
                                    )}
                                </p>
                            )}

                            {correctionError != null && (
                                <p className="text-center text-sm text-red-500">
                                    {correctionError}
                                </p>
                            )}

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        void hardware.haptic();
                                        setAnalysis(null);
                                        setCorrectionError(null);
                                    }}
                                    className="
                                        flex-1 rounded-full bg-gray-100 px-5 py-3
                                        font-medium text-gray-700 transition-colors
                                        hover:bg-gray-200
                                    "
                                >
                                    Back
                                </button>
                                <AsyncButton
                                    onClick={handleApproveCorrection}
                                    className="
                                        flex-1 rounded-full bg-green-500 px-5 py-3
                                        font-medium text-white transition-colors
                                        hover:bg-green-600
                                    "
                                >
                                    Apply
                                </AsyncButton>
                            </div>
                        </>
                    )}
                </div>
            </>
        );
    }

    return (
        <>
            {/* Image preview */}
            {imageUrl != null ? (
                <div className="aspect-square bg-gray-100">
                    <img
                        src={imageUrl}
                        alt={entry.name}
                        className="size-full object-cover"
                    />
                </div>
            ) : (
                <div
                    className="
                        flex aspect-square items-center justify-center bg-gray-100
                    "
                >
                    <CameraIcon className="size-16 text-gray-300" />
                </div>
            )}

            {/* Entry info */}
            <div className="space-y-4 p-4">
                <h3 className="text-center text-2xl font-normal text-gray-900">
                    {entry.name}
                </h3>

                {/* Quantity and unit editing for barcode items */}
                {isBarcode && (
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <DecimalInput
                                name="quantity"
                                ariaLabel="quantity"
                                onChange={setQuantity}
                                value={quantity}
                            />
                        </div>
                        <div className="flex-1">
                            <QuickSelect
                                name="unit"
                                type="text"
                                options={entry.units.map((u) => ({
                                    value: u.unit,
                                    label: formatUnit(u.unit),
                                }))}
                                onChange={(nextUnit) => {
                                    if (nextUnit != null) {
                                        setUnit(nextUnit);
                                    }
                                }}
                                value={unit}
                                shrinkLast={false}
                            />
                        </div>
                    </div>
                )}

                {/* Buttons: Delete/Fix on left, Close/Save on right */}
                <div className="flex items-center justify-between gap-4">
                    <div className="flex gap-2">
                        <MultiStateDeleteButton onDelete={onDelete} onClose={onClose} />
                        {isPhotoEntry && (
                            <button
                                type="button"
                                onClick={() => {
                                    void hardware.haptic();
                                    setIsCorrectMode(true);
                                }}
                                className="
                                    rounded-full bg-amber-500 px-5 py-3 font-medium
                                    text-white transition-colors
                                    hover:bg-amber-600
                                "
                            >
                                Fix
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                void hardware.haptic();
                                onClose();
                            }}
                            className="
                                rounded-full bg-gray-100 px-5 py-3 font-medium
                                text-gray-700 transition-colors
                                hover:bg-gray-200
                            "
                        >
                            Close
                        </button>
                        {isBarcode && (
                            <button
                                type="button"
                                onClick={() => {
                                    void hardware.haptic();
                                    handleSave();
                                }}
                                disabled={isSaving || quantity == null}
                                className="
                                    rounded-full bg-indigo-500 px-5 py-3 font-medium
                                    text-white transition-colors
                                    hover:bg-indigo-600
                                    disabled:cursor-not-allowed disabled:bg-gray-300
                                "
                            >
                                {isSaving ? "Saving..." : "Save"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

function ShimmerBlock() {
    return (
        <div className="h-[13px] w-[56px] animate-pulse rounded-full bg-stone-200" />
    );
}

interface EntryRowProps {
    entry: server.journal.nutrition.ExpandedEntry.output;
    onClick: () => void;
}

function entryAriaLabel(resolved: ReturnType<typeof resolveEntry>): string {
    if (resolved.kind === "processing") return "Analyzing entry";
    if (resolved.kind === "identified") return `Identified as ${resolved.name}`;
    if (resolved.kind === "error") return "Error processing entry";
    return resolved.name;
}

function EntryRow({entry, onClick}: EntryRowProps) {
    const resolved = resolveEntry(entry);

    return (
        <button
            type="button"
            id={entry.uuid}
            aria-label={entryAriaLabel(resolved)}
            onClick={() => {
                void hardware.haptic();
                onClick();
            }}
            disabled={resolved.kind === "processing" || resolved.kind === "identified"}
            className="
                relative flex h-12 w-full flex-col justify-center px-4 text-left
                transition-colors
                hover:bg-gray-50
                disabled:cursor-default
                disabled:hover:bg-transparent
            "
        >
            <div className="flex h-5 items-center justify-between">
                <div className="min-w-0 flex-1">
                    <AnimatePresence mode="wait" initial={false}>
                        {resolved.kind === "error" ? (
                            <motion.span
                                key="error"
                                initial={{opacity: 0, x: -10}}
                                animate={{opacity: 1, x: 0}}
                                exit={{opacity: 0, x: 10}}
                                transition={{duration: 0.2}}
                                className="block truncate text-sm"
                                style={{color: "#ef4444"}}
                            >
                                Error processing image
                            </motion.span>
                        ) : resolved.kind === "processing" ? (
                            <motion.span
                                key="analyzing"
                                initial={{opacity: 0, x: -10}}
                                animate={{opacity: 1, x: 0}}
                                exit={{opacity: 0, x: 10}}
                                transition={{duration: 0.2}}
                                className="
                                    -mt-0.5 block truncate text-sm font-light
                                    text-stone-600
                                "
                            >
                                Analyzing…
                            </motion.span>
                        ) : (
                            <motion.span
                                key="name"
                                initial={{opacity: 0, x: -10}}
                                animate={{opacity: 1, x: 0}}
                                exit={{opacity: 0, x: 10}}
                                transition={{duration: 0.2}}
                                className="block truncate text-[14.5px] font-medium"
                                style={{color: "#3d3a35"}}
                            >
                                {resolved.name}
                            </motion.span>
                        )}
                    </AnimatePresence>
                </div>
                <div className="shrink-0 pl-4">
                    <AnimatePresence mode="wait" initial={false}>
                        {resolved.kind === "identified" ? (
                            <motion.div
                                key="shimmer"
                                initial={{opacity: 0, scale: 0.8}}
                                animate={{opacity: 1, scale: 1}}
                                exit={{opacity: 0, scale: 0.8}}
                                transition={{duration: 0.2}}
                            >
                                <ShimmerBlock />
                            </motion.div>
                        ) : resolved.kind === "processing" ? (
                            <motion.span key="spacer" />
                        ) : (
                            <motion.span
                                key="serving"
                                initial={{opacity: 0, scale: 0.8}}
                                animate={{opacity: 1, scale: 1}}
                                exit={{opacity: 0, scale: 0.8}}
                                transition={{duration: 0.2}}
                                className="text-sm text-stone-600"
                            >
                                {getRoundedQuantity(entry.quantity)} {entry.unit}
                            </motion.span>
                        )}
                    </AnimatePresence>
                </div>
            </div>
            {(resolved.kind === "processing" || resolved.kind === "identified") && (
                <div
                    className="
                        absolute inset-x-4 bottom-2.5 h-[2px] overflow-hidden
                        rounded-full bg-stone-200
                    "
                >
                    <motion.div
                        className="h-full w-[30%] rounded-full bg-indigo-500"
                        animate={{x: ["-100%", "350%"]}}
                        transition={{
                            duration: 1.4,
                            ease: "easeInOut",
                            repeat: Infinity,
                        }}
                    />
                </div>
            )}
        </button>
    );
}

interface LearningMealCardProps {
    mealName: LearningWeekMeal;
    meal: server.journal.nutrition.Meal.output | null;
    onAddEntry: () => Promise<void>;
    onEntryClick: (entry: server.journal.nutrition.ExpandedEntry.output) => void;
}

function LearningMealCard(props: LearningMealCardProps) {
    const {mealName, meal, onAddEntry, onEntryClick} = props;
    const hasEntries = meal != null && meal.entries.length > 0;
    const entryCount = meal?.entries.length ?? 0;

    return (
        <div
            role="region"
            aria-label={mealName}
            className="overflow-hidden rounded-xl bg-white"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-2 py-2.5">
                <div className="flex items-center gap-2">
                    <div
                        className="
                            flex size-8 items-center justify-center rounded-full
                            bg-red-100
                            *:size-4
                        "
                    >
                        {mealName === "Breakfast" && <Breakfast />}
                        {mealName === "Lunch" && <Lunch />}
                        {mealName === "Dinner" && <Dinner />}
                        {mealName === "Snacks" && <Snack />}
                    </div>
                    <h3 className="text-base font-normal text-gray-900">{mealName}</h3>
                    <AnimatePresence mode="wait" initial={false}>
                        {hasEntries && (
                            <motion.span
                                key="count"
                                initial={{opacity: 0, scale: 0.8}}
                                animate={{opacity: 1, scale: 1}}
                                exit={{opacity: 0, scale: 0.8}}
                                transition={{duration: 0.15}}
                                className="
                                    rounded-lg bg-gray-100 px-2.5 py-0.75 text-[13px]
                                    font-medium text-gray-900 ring-1 ring-gray-100
                                    ring-inset
                                "
                            >
                                {entryCount} {entryCount === 1 ? "item" : "items"}
                            </motion.span>
                        )}
                    </AnimatePresence>
                </div>
                <div className="flex items-center gap-1.5">
                    <AsyncButton
                        onClick={() => {
                            void hardware.haptic();
                            return onAddEntry();
                        }}
                        className={classNames(
                            "inline-flex items-center text-sm font-semibold transition-all",
                            "bg-indigo-100 text-indigo-600 hover:bg-gray-50",
                            "disabled:opacity-50 disabled:cursor-not-allowed",
                            "justify-center rounded-full size-8",
                        )}
                        aria-label={`Log ${mealName}`}
                    >
                        <PlusIcon className="size-5" />
                    </AsyncButton>
                </div>
            </div>

            {/* Divider between header and content */}
            <div className="mx-4 border-b border-gray-100" />

            {/* Entries list or empty state */}
            <AnimatePresence mode="wait" initial={false}>
                {hasEntries ? (
                    <motion.div
                        key="entries"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.15}}
                    >
                        {meal.entries.map((entry, index) => (
                            <React.Fragment key={entry.uuid}>
                                {index > 0 && (
                                    <div className="mx-4 border-b border-gray-100" />
                                )}
                                <EntryRow
                                    entry={entry}
                                    onClick={() => onEntryClick(entry)}
                                />
                            </React.Fragment>
                        ))}
                    </motion.div>
                ) : (
                    <motion.div
                        key="empty"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.15}}
                        className="flex h-12 items-center justify-center gap-2 px-4"
                    >
                        <span className="text-base text-gray-500">
                            No {mealName.toLowerCase()} logged
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

interface TourCardProps {
    onDismiss: () => void;
}

function TourCard({onDismiss}: TourCardProps) {
    const [isVisible, setIsVisible] = React.useState(true);

    return (
        <AnimatePresence initial={false}>
            {isVisible && (
                <motion.div
                    initial={{opacity: 0, scale: 0}}
                    animate={{opacity: 1, scale: 1}}
                    exit={{opacity: 0, scale: 0}}
                    transition={{duration: 0.2}}
                    className="overflow-hidden rounded-xl bg-indigo-50"
                >
                    <div className="flex items-center px-2 py-2.5">
                        <div
                            className="
                                flex size-16 items-center justify-center fill-indigo-100
                                text-indigo-600
                            "
                        >
                            <Lottie
                                animationData={rocketAnimation}
                                loop={true}
                                className="current-color-lottie"
                            />
                        </div>
                        <div className="-ml-4 flex-1 text-center">
                            <h3 className="text-base font-normal text-indigo-900">
                                You're all set
                            </h3>
                            <p className="text-base text-indigo-500">
                                Tap{" "}
                                <PlusIcon className="inline-block size-4 text-indigo-600" />{" "}
                                on any meal below
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                void hardware.haptic();
                                setIsVisible(false);
                                setTimeout(() => {
                                    onDismiss();
                                }, 200);
                            }}
                            className="
                                rounded-full bg-indigo-100 px-4 py-2 text-sm font-medium
                                text-indigo-600
                            "
                        >
                            Got it
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function LearningWeek(props: Props) {
    const setJournal = (updated: server.journal.day.Journal.output) => {
        props.setResolved(() => ({
            journal: updated,
        }));
    };

    const entryDetailDialog = dialogs.useDialog<
        "deleted" | null,
        {entry: server.journal.nutrition.ExpandedEntry.output}
    >({
        untestedIsOpenMigrateIfWorkingOnThisComponent: true,
        Content: ({entry, close}) => {
            const imageUid = entry.nutritional_vision_prompt?.image?.image_uid;
            const imageUrl =
                imageUid != null
                    ? `https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${imageUid}/public`
                    : null;

            const isBarcode = entry.nutritional_vision_prompt?.type === "BARCODE";
            const promptStatus = entry.nutritional_vision_prompt?.status;

            const handleDelete = () => {
                void analytics.track("DELETE_JOURNAL_ENTRY_SUCCESS");

                const updatedJournal = journal.deleteEntryInJournal({
                    journal: props.resolved.journal,
                    entry,
                    deleteMealIfEmpty: true,
                });

                setJournal(updatedJournal);
                return Promise.resolve();
            };

            // Barcode still processing - show loading state
            if (isBarcode && promptStatus === "PROCESSING") {
                return (
                    <>
                        {imageUrl != null ? (
                            <div className="relative aspect-square bg-gray-100">
                                <img
                                    src={imageUrl}
                                    alt="Scanned barcode"
                                    className="size-full object-cover"
                                />
                                <div
                                    className="
                                        absolute inset-0 flex items-center
                                        justify-center bg-black/30
                                    "
                                >
                                    <Spinner />
                                </div>
                            </div>
                        ) : (
                            <div
                                className="
                                    flex aspect-square items-center justify-center
                                    bg-gray-100
                                "
                            >
                                <Spinner />
                            </div>
                        )}
                        <div className="space-y-4 p-4">
                            <h3
                                className="
                                    text-center text-lg font-semibold text-gray-900
                                "
                            >
                                Looking up barcode...
                            </h3>
                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => {
                                        void hardware.haptic();
                                        close(null);
                                    }}
                                    className="
                                        rounded-full bg-gray-100 px-5 py-3 font-medium
                                        text-gray-700 transition-colors
                                        hover:bg-gray-200
                                    "
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </>
                );
            }

            // Barcode lookup failed - show error with delete option
            if (isBarcode && promptStatus === "ERROR") {
                return (
                    <>
                        {imageUrl != null ? (
                            <div className="aspect-square bg-gray-100">
                                <img
                                    src={imageUrl}
                                    alt="Scanned barcode"
                                    className="size-full object-cover"
                                />
                            </div>
                        ) : (
                            <div
                                className="
                                    flex aspect-square items-center justify-center
                                    bg-gray-100
                                "
                            >
                                <CameraIcon className="size-16 text-gray-300" />
                            </div>
                        )}
                        <div className="space-y-4 p-4">
                            <h3
                                className="
                                    text-center text-lg font-semibold text-gray-900
                                "
                            >
                                Product not found
                            </h3>
                            <p className="text-center text-sm text-gray-500">
                                We couldn't find this product in our database.
                            </p>

                            <div className="flex justify-center gap-2">
                                <MultiStateDeleteButton
                                    onDelete={handleDelete}
                                    onClose={() => close("deleted")}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        void hardware.haptic();
                                        close(null);
                                    }}
                                    className="
                                        rounded-full bg-gray-100 px-5 py-3 font-medium
                                        text-gray-700 transition-colors
                                        hover:bg-gray-200
                                    "
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </>
                );
            }

            // Regular entry view (SUCCESS or non-barcode)
            return (
                <RegularEntryView
                    entry={entry}
                    imageUrl={imageUrl}
                    isBarcode={isBarcode}
                    onDelete={handleDelete}
                    onClose={() => close(null)}
                    journal={props.resolved.journal}
                    setJournal={setJournal}
                />
            );
        },
    });

    const {
        start,
        handlePhoto,
        dialog: captureDialog,
    } = useCapture({
        target: {type: "DAY", day: props.resolved.journal.day},
        profile: props.profile,
    });

    const learningWeekSetJournal = (
        updater: (prev: typeof props.resolved.journal) => typeof props.resolved.journal,
    ) => {
        props.setResolved((prevState) => ({
            journal: updater(prevState.journal),
        }));
    };

    const handlePhotoOptions = (mealName: string) => ({
        currentJournal: props.resolved.journal,
        setJournal: learningWeekSetJournal,
        preSelectedMealName: mealName,
        onProcessed: async ({
            result,
            captureType,
        }: {
            result: ProcessResult;
            captureType: "meal" | "barcode" | "facts";
        }) => {
            if (result.kind !== "complete" || captureType !== "barcode") {
                return;
            }

            const imageUid = result.entry.nutritional_vision_prompt?.image?.image_uid;

            if (imageUid != null) {
                const img = new Image();
                img.src = `https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${imageUid}/public`;
                await new Promise<void>((resolve) => {
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                });
            }

            void entryDetailDialog({entry: result.entry});
        },
    });

    const handleCaptureResult = (result: CaptureResult, mealName: string) => {
        switch (result.type) {
            case "meal_image":
            case "facts_image":
                handlePhoto(
                    result.prompt,
                    result.type === "meal_image" ? "meal" : "facts",
                    handlePhotoOptions(mealName),
                );
                break;
            case "barcode":
                handlePhoto(result.prompt, "barcode", handlePhotoOptions(mealName));
                break;
            case "entries":
                if (result.method === "cupboard") {
                    journal.addEntriesToCupboard({
                        entries: result.entries,
                        dispatch: props.dispatch,
                    });
                } else {
                    const {updatedJournal} = journal.addEntriesToJournal({
                        journal: props.resolved.journal,
                        meal: result.meal,
                        entries: result.entries,
                        journal_mode: "SEARCH",
                        dispatch: props.dispatch,
                        updated: (updatedStreak) => {
                            props.setResolved((prevState) => ({
                                journal: {
                                    ...prevState.journal,
                                    streak: updatedStreak,
                                },
                            }));
                        },
                    });
                    props.dispatch(showFeelerIfFirstTime());
                    props.setResolved(() => ({journal: updatedJournal}));
                }
                break;
            case "canceled":
                break;
        }
    };

    const preloadedImages = React.useRef(new Set<string>());

    React.useEffect(() => {
        const entries = props.resolved.journal.day.meals.flatMap((m) => m.entries);

        for (const entry of entries) {
            const imageUid = entry.nutritional_vision_prompt?.image?.image_uid;
            if (imageUid != null && !preloadedImages.current.has(imageUid)) {
                const img = new Image();
                img.src = `https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${imageUid}/public`;
                preloadedImages.current.add(imageUid);
            }
        }
    }, [props.resolved.journal]);

    const getMealByName = (name: LearningWeekMeal) => {
        return props.resolved.journal.day.meals.find((m) => m.name === name) ?? null;
    };

    return (
        <>
            {entryDetailDialog.dialog}
            {captureDialog}

            <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                <Steps
                    profile={props.profile}
                    journal={props.resolved.journal}
                    setJournal={setJournal}
                    forceLinkedToAppleHealth={true}
                />
                <WeighIn
                    profile={props.profile}
                    journal={props.resolved.journal}
                    setJournal={setJournal}
                />
            </div>

            <div className="mt-2.5 flex flex-col gap-2.5">
                {!props.profile.user.casted_dismissed_announcements.includes(
                    "LEARNING_WEEK_TOUR",
                ) && (
                    <TourCard
                        onDismiss={() => {
                            void props.dispatch(
                                orm.dismissAnnouncement("LEARNING_WEEK_TOUR"),
                            );
                        }}
                    />
                )}
                {LEARNING_WEEK_MEALS.map((mealName) => (
                    <LearningMealCard
                        key={mealName}
                        mealName={mealName}
                        meal={getMealByName(mealName)}
                        onAddEntry={async () => {
                            const result = await start({
                                initialView: "camera",
                                preSelectedMealName: mealName,
                            });
                            handleCaptureResult(result, mealName);
                        }}
                        onEntryClick={(entry) => {
                            void entryDetailDialog({entry});
                        }}
                    />
                ))}
            </div>
        </>
    );
}

function Group({
    journal,
    groupTab,
}: {
    journal: server.journal.day.Journal.output;
    groupTab: GroupTab;
}) {
    const learningWeek = journal.learning_week;
    const liveMetrics = useAppSelector((state) => orm.getMetrics(state, journal));

    const learningWeekStartDate = React.useMemo(
        () => (learningWeek != null ? dates.parseISO(learningWeek.start_date) : null),
        [learningWeek?.start_date],
    );

    const currentDayNumber = learningWeek?.current_day_number ?? 1;

    const weekDays = React.useMemo(
        () =>
            learningWeekStartDate != null
                ? getWeekDays(learningWeekStartDate, currentDayNumber)
                : null,
        [learningWeekStartDate?.getTime(), currentDayNumber],
    );

    if (learningWeek == null || weekDays == null) {
        return null;
    }

    // todayIndex is the 0-based index into the days array (which now has currentDayNumber elements)
    // Can be negative if learning week hasn't started yet
    const todayIndex = learningWeek.current_day_number - 1;
    const emptyDayStatus = {
        date: "",
        has_logged_meals: false,
        has_logged_weight: false,
        has_hit_steps: false,
        steps: null,
    };

    if (groupTab === "today") {
        return (
            <div className="flex flex-col">
                {/* Task header row - aligned with circles below */}
                <div className="mb-2 flex items-center px-4">
                    <div className="w-24 shrink-0" />
                    <div className="flex flex-1 justify-around">
                        {["Food", "Weight", "Steps"].map((label) => (
                            <span
                                key={label}
                                className="
                                    w-11 text-center text-xs font-normal text-gray-700
                                "
                            >
                                {label}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Member rows */}
                {learningWeek.group_statuses.map((member) => {
                    const todayStatus = member.days[todayIndex] ?? emptyDayStatus;

                    const isCurrentUser =
                        member.member_uuid === learningWeek.my_status.member_uuid;
                    const isCurrentDay = todayStatus.date === journal.day.key;
                    const useLiveSteps = isCurrentUser && isCurrentDay;

                    const steps = useLiveSteps
                        ? (liveMetrics.STEPS ?? todayStatus.steps)
                        : todayStatus.steps;
                    const hasHitSteps = useLiveSteps
                        ? (liveMetrics.STEPS ?? 0) >= learningWeek.steps_goal
                        : todayStatus.has_hit_steps;

                    const formatSteps = (value: number | null) => {
                        if (value == null) return null;
                        if (value >= 1000) {
                            return `${Math.round(value / 1000)}k`;
                        }
                        return value.toString();
                    };

                    const tasks = [
                        {done: todayStatus.has_logged_meals, content: "check"},
                        {done: todayStatus.has_logged_weight, content: "check"},
                        {
                            done: hasHitSteps,
                            content: (
                                <span className="text-base font-light">
                                    {formatSteps(steps)}
                                </span>
                            ),
                        },
                    ];

                    return (
                        <div
                            key={member.member_uuid}
                            className="
                                mb-2.5 flex items-center rounded-2xl bg-white px-4 py-3
                            "
                        >
                            <span
                                className="
                                    w-24 shrink-0 truncate text-sm font-normal
                                    text-gray-900
                                "
                            >
                                {member.display_name}
                            </span>
                            <div className="flex flex-1 justify-around">
                                {tasks.map((task, index) => (
                                    <div key={index} className="relative size-11">
                                        {task.done ? (
                                            <>
                                                <div className="absolute inset-0">
                                                    <ProgressRing progress={1} />
                                                </div>
                                                <div
                                                    className="
                                                        absolute inset-0 z-10 flex
                                                        items-center justify-center
                                                        rounded-full bg-indigo-600
                                                    "
                                                >
                                                    {task.content === "check" ? (
                                                        <CheckIcon
                                                            className="
                                                                size-5 text-white
                                                            "
                                                        />
                                                    ) : (
                                                        <span
                                                            className="
                                                                text-[10px] font-medium
                                                                text-white
                                                            "
                                                        >
                                                            {task.content}
                                                        </span>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="absolute inset-0">
                                                    <DashedCircle />
                                                </div>
                                                {task.content !== "check" && (
                                                    <span
                                                        className="
                                                            absolute inset-0 z-10 flex
                                                            items-center justify-center
                                                            text-[10px] font-medium
                                                            text-gray-500
                                                        "
                                                    >
                                                        {task.content}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            {/* Weekday header at the top */}
            <div className="mx-1 mb-2 grid grid-cols-7 gap-1">
                {weekDays.map((item) => (
                    <div key={item.dateString} className="flex justify-center">
                        <span className="text-xs font-normal text-gray-700">
                            {item.dayAbbrev}
                        </span>
                    </div>
                ))}
            </div>

            {/* Member rows: circles then name */}
            {learningWeek.group_statuses.map((member) => (
                <div
                    key={member.member_uuid}
                    className="mb-2.5 flex flex-col rounded-2xl bg-white px-4 py-2.5"
                >
                    <span className="mb-1 text-sm font-normal text-gray-900">
                        {member.display_name}
                    </span>
                    <div className="-mx-3 grid grid-cols-7 gap-1">
                        {weekDays.map((item) => {
                            // Access by actual day number (1-based) converted to 0-based index
                            const dayStatus = member.days[item.dayNumber - 1];
                            const hasMeals = dayStatus.has_logged_meals;
                            const hasWeight = dayStatus.has_logged_weight;
                            const hasComplete = hasMeals && hasWeight;
                            const hasPartial = !hasComplete && (hasMeals || hasWeight);

                            return (
                                <div
                                    key={item.dateString}
                                    className="flex justify-center"
                                >
                                    <div className="relative size-8">
                                        {hasComplete ? (
                                            <div className="absolute inset-0">
                                                <ProgressRing progress={1} />
                                            </div>
                                        ) : hasPartial ? (
                                            <div className="absolute inset-0">
                                                <DashedCircle indigo />
                                            </div>
                                        ) : (
                                            <div className="absolute inset-0">
                                                <DashedCircle />
                                            </div>
                                        )}
                                        {hasComplete ? (
                                            <div
                                                className="
                                                    absolute inset-0 z-10 flex
                                                    items-center justify-center
                                                    rounded-full bg-indigo-600
                                                "
                                            >
                                                <CheckIcon className="size-4 text-white" />
                                            </div>
                                        ) : (
                                            <span
                                                className="
                                                    absolute inset-0 z-10 flex
                                                    items-center justify-center text-xs
                                                    font-light text-gray-600
                                                "
                                            >
                                                {item.dayNumber}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

function Profile(props: {
    profileHandler: ReturnType<typeof useProfile>;
    onSupportClick: () => void;
}) {
    return (
        <div className="flex h-full flex-col gap-8 pt-4">
            {props.profileHandler.component}
            <button
                type="button"
                onClick={props.onSupportClick}
                className="rounded-2xl bg-stone-300/50 px-6 py-3 font-medium"
            >
                Get Help
            </button>
        </div>
    );
}

export function useLearningWeek(props: Props): JournalImplementation {
    const dispatch = useAppDispatch();
    const profileHandler = useProfile({
        profile: props.profile,
        lastWeight: null,
        withName: true,
        withWeight: false,
    });

    const mountedRef = React.useRef(true);

    React.useEffect(() => {
        mountedRef.current = true;

        const listener = hardware.App.addListener("appStateChange", (appState) => {
            if (appState.isActive) {
                void (async () => {
                    const result = await server.journal.day.journal({
                        key: props.resolved.journal.day.key,
                        user_id: props.resolved.journal.day.user_id,
                    });
                    if (mountedRef.current && result.type === "success") {
                        props.setResolved(() => ({journal: result.data}));
                    }
                })();
            }
        });

        return () => {
            mountedRef.current = false;
            void listener.then((l) => l.remove());
        };
    }, [props.resolved.journal.day.key, props.resolved.journal.day.user_id]);

    const saveProfile = async () => {
        if (!profileHandler.isComplete) {
            alert("Please fill out all fields");
            return;
        }
        const result = await server.core.rpc.update_profile({
            email: props.profile.user.email,
            name: profileHandler.form.values.name,
            date_of_birth: profileHandler.form.values.date_of_birth,
            gender: profileHandler.form.values.gender == "MALE" ? "m" : "f",
            use_kilojoules: false,
            is_imperial: profileHandler.form.values.units === "IMPERIAL",
            weight: null,
            height: profileHandler.normalizedHeight,
            waist: null,
            body_fat: null,
            activity: null,
            goal: null,
        });
        if (result.type === "success") {
            alert("Profile updated.");
        }
    };

    const [groupTab, setGroupTab] = React.useState<GroupTab>("today");

    const showGroupTab =
        (props.resolved.journal.learning_week?.group_statuses.length ?? 0) >= 2;

    return {
        tabs: {
            journal: {
                view: (
                    <>
                        <DaySelector journal={props.resolved.journal} />
                        <LearningWeek {...props} />
                    </>
                ),
                title: "Home",
                icon: HomeIcon,
            },
            group: {
                view: (
                    <>
                        <GroupTabSection
                            selectedTab={groupTab}
                            onSelectTab={setGroupTab}
                        />
                        <Group journal={props.resolved.journal} groupTab={groupTab} />
                    </>
                ),
                title: "Group",
                icon: UserGroupIcon,
                isHidden: !showGroupTab,
            },
            summary: null,
            workout: null,
            profile: {
                view: (
                    <Profile
                        profileHandler={profileHandler}
                        onSupportClick={() => {
                            router.push(
                                "journal_contact.support",
                                {key: props.resolved.journal.day.key},
                                props.resolved,
                            );
                        }}
                    />
                ),
                title: "Profile",
                icon: UserIcon,
            },
        },
        actions: [],
        options: {
            titleSection: (
                <div className="relative flex w-full items-center justify-center py-1">
                    {props.currentTab === "profile" && (
                        <div
                            className="
                                absolute inset-y-0 left-0 flex items-center
                                justify-center
                            "
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    void hardware.haptic();
                                    void dispatch(auth.logout());
                                }}
                                className="font-medium"
                            >
                                Logout
                            </button>
                        </div>
                    )}
                    <graphics.Logo className="h-10 w-auto py-1.5" />
                    {props.currentTab === "profile" && (
                        <div
                            className="
                                absolute inset-y-0 right-0 flex items-center
                                justify-center
                            "
                        >
                            <AsyncButton
                                type="button"
                                onClick={async () => {
                                    // eslint-disable-next-line @typescript-eslint/await-thenable
                                    await hardware.haptic();
                                    await saveProfile();
                                }}
                                className="
                                    rounded-2xl bg-stone-300/50 px-6 py-1.5 font-medium
                                "
                            >
                                Save
                            </AsyncButton>
                        </div>
                    )}
                </div>
            ),
            title: "Learning Week",
            scene: {kind: "SIMPLE", nav: "none"},
        },
    };
}

type ContactFormProps = {
    label: string;
    form: PFormHandler<server.core.rpc.Contact>;
    isEmailDisabled: boolean;
};

const ContactForm = (props: ContactFormProps) => {
    return (
        <div style={{display: "flex", flexDirection: "column", gap: 25}}>
            <Label
                label={props.form.fields.email.label}
                error={props.form.errors?.email?.[0] ?? null}
            >
                <PWidget
                    field={{
                        ...props.form.fields.email,
                        readOnly: props.isEmailDisabled,
                    }}
                />
            </Label>
            <Label label={props.label} error={props.form.errors?.message?.[0] ?? null}>
                <PWidget field={props.form.fields.message} />
            </Label>
        </div>
    );
};

export const JournalContact = router.JournalContact.useConditions((props) => {
    const form = useAutoPform(server.core.rpc.Contact, {
        email: props.profile.user.email,
        message: "",
        type: "SUPPORT",
    });
    const manager_name = props.profile.user.manager_name;

    const {type, message} =
        props.currentTab == "feedback"
            ? ({
                  type: "FEEDBACK",
                  message:
                      "Thank you for your feedback. \n\nWe'll get back to you with our thoughts soon. \n\nCheck your spam folder in case our reply ends up there.",
              } as const)
            : props.currentTab == "manager"
              ? ({
                    type: "MANAGER",
                    message: `Your message was sent to ${manager_name}.`,
                } as const)
              : ({
                    type: "SUPPORT",
                    message:
                        "Your message was sent to our support team. \n\nWe'll get back to you within a day. \n\nCheck your spam folder in case our reply ends up there.",
                } as const);

    const onSubmit = async () => {
        const result = await submitForm(
            server.core.rpc.contact,
            {...form.values, type},
            form,
        );

        if (result.type === "success") {
            form.reset();
            alert(message);
        }
    };

    return {
        view: (
            <ContactForm
                form={form}
                label="Technical issues or questions about the app"
                isEmailDisabled={true}
            />
        ),
        actions: [
            {
                icon: "send",
                title: "Submit",
                onClick: onSubmit,
            },
        ],
        options: {
            back: "Home",
            title: "Support",
            scene: {kind: "SIMPLE", nav: "none"},
        },
    };
});
